import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Crown, MessageSquare, Layers, Shield, Bot, BookOpen, LayoutDashboard } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";

const DISCORD_INVITE_LINK = "https://discord.com/oauth2/authorize?client_id=1324844843670835240";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const ListItem = ({
    title,
    href,
    description,
    icon,
  }: {
    title: string;
    href: string;
    description: string;
    icon?: React.ReactNode;
  }) => {
    return (
      <li>
        <NavigationMenuLink asChild>
          <Link
            to={href}
            className={cn(
              "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
            )}
          >
            <div className="flex items-center gap-2 text-sm font-medium leading-none">
              {icon}
              {title}
            </div>
            <p className="line-clamp-2 mt-2 text-sm leading-snug text-muted-foreground">
              {description}
            </p>
          </Link>
        </NavigationMenuLink>
      </li>
    );
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/90 dark:bg-gray-900/90 backdrop-blur-md shadow-sm py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <span className="text-2xl font-bold text-fembot-pink">Fembot</span>
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={`font-medium transition-colors dark:text-gray-200 px-4 ${isActive('/features') ? 'text-fembot-pink' : 'hover:text-fembot-pink'}`}
                  >
                    <Layers className="h-4 w-4 mr-1.5" /> Features
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid gap-3 p-6 w-[450px] grid-cols-2">
                      <ListItem
                        href="/security-tools"
                        title="Security Tools"
                        description="Protect your server with anti-raid and verification systems"
                        icon={<Shield className="h-4 w-4 text-fembot-pink" />}
                      />
                      <ListItem
                        href="/moderation"
                        title="Moderation"
                        description="Advanced tools to keep your community safe"
                        icon={<Shield className="h-4 w-4 text-fembot-pink" />}
                      />
                      <ListItem
                        href="/ai-features"
                        title="AI Features"
                        description="Engage with intelligent chatbot and smart responses"
                        icon={<Bot className="h-4 w-4 text-fembot-pink" />}
                      />
                      <ListItem
                        href="/engagement"
                        title="Engagement"
                        description="Fun games and interaction commands for your community"
                        icon={<MessageSquare className="h-4 w-4 text-fembot-pink" />}
                      />
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <Link 
              to="/premium" 
              className={`font-medium transition-colors dark:text-gray-200 flex items-center ${isActive('/premium') ? 'text-fembot-pink' : 'hover:text-fembot-pink'}`}
            >
              <Crown className="h-4 w-4 mr-1.5 text-amber-500" /> 
              Premium
            </Link>

            <Link 
              to="/blog" 
              className={`font-medium transition-colors dark:text-gray-200 flex items-center ${isActive('/blog') ? 'text-fembot-pink' : 'hover:text-fembot-pink'}`}
            >
              <BookOpen className="h-4 w-4 mr-1.5" />
              Blog
            </Link>

            <Link 
              to="/support" 
              className={`font-medium transition-colors dark:text-gray-200 flex items-center ${isActive('/support') ? 'text-fembot-pink' : 'hover:text-fembot-pink'}`}
            >
              <MessageSquare className="h-4 w-4 mr-1.5" />
              Support
            </Link>

            <Link 
              to="/dashboard" 
              className={`font-medium transition-colors dark:text-gray-200 flex items-center ${isActive('/dashboard') ? 'text-fembot-pink' : 'hover:text-fembot-pink'}`}
            >
              <LayoutDashboard className="h-4 w-4 mr-1.5" />
              Dashboard
            </Link>

            <ThemeToggle />
            
            <Button 
              className="btn-primary" 
              onClick={() => window.location.href = "/info"}
            >
              Add to Discord
            </Button>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <ThemeToggle />
            <button
              className="text-gray-700 dark:text-gray-300 hover:text-fembot-pink dark:hover:text-fembot-pink"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-white dark:bg-gray-900 shadow-lg py-5 px-4 animate-fade-in">
            <div className="flex flex-col space-y-4">
              <Link
                to="/features"
                className={`font-medium transition-colors py-2 dark:text-gray-200 flex items-center ${isActive('/features') ? 'text-fembot-pink' : 'hover:text-fembot-pink'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Layers className="h-4 w-4 mr-1.5" /> 
                Features
              </Link>
              <Link
                to="/premium"
                className={`font-medium transition-colors py-2 dark:text-gray-200 flex items-center ${isActive('/premium') ? 'text-fembot-pink' : 'hover:text-fembot-pink'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Crown className="h-4 w-4 mr-1.5 text-amber-500" />
                Premium
              </Link>
              <Link
                to="/blog"
                className={`font-medium transition-colors py-2 dark:text-gray-200 flex items-center ${isActive('/blog') ? 'text-fembot-pink' : 'hover:text-fembot-pink'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <BookOpen className="h-4 w-4 mr-1.5" />
                Blog
              </Link>
              <Link
                to="/support"
                className={`font-medium transition-colors py-2 dark:text-gray-200 flex items-center ${isActive('/premium') ? 'text-fembot-pink' : 'hover:text-fembot-pink'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <MessageSquare className="h-4 w-4 mr-1.5" />
                Support
              </Link>
              <Link
                to="/dashboard"
                className={`font-medium transition-colors py-2 dark:text-gray-200 flex items-center ${isActive('/dashboard') ? 'text-fembot-pink' : 'hover:text-fembot-pink'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <LayoutDashboard className="h-4 w-4 mr-1.5" />
                Dashboard
              </Link>
              <Button 
                className="btn-primary w-full"
                 onClick={() => window.location.href = "/info"}
              >
                Add to Discord
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
